import { useCallback } from "react";
import { useAuth } from "../store/AuthContext";
import {
  logInvoiceEvent,
  type InvoiceAuditLogPayload,
} from "../Services/InvoiceAuditLog";

/** Payload provided by callers; actor and timestamp are filled in by the hook. */
export type InvoiceAuditEventInput = Omit<
  InvoiceAuditLogPayload,
  "performedById" | "performedByName" | "performedByEmail" | "performedAt"
> & { performedAt?: string };

/**
 * Provides a best-effort logger for invoice/payment audit events. The logged-in
 * user (from AuthContext) is attached automatically. Failures are swallowed so
 * logging never blocks the underlying accounting action.
 */
export function useInvoiceAuditLogger(): {
  logEvent: (input: InvoiceAuditEventInput) => Promise<void>;
} {
  const { loggedPerson, tokenDecoded } = useAuth();

  const logEvent = useCallback(
    async (input: InvoiceAuditEventInput): Promise<void> => {
      const performedByName =
        (loggedPerson
          ? [
              loggedPerson.honorary,
              loggedPerson.name,
              loggedPerson.middleInitial,
              loggedPerson.lastName,
              loggedPerson.suffix,
            ]
              .filter((part) => part)
              .join(" ")
              .trim()
          : "") ||
        tokenDecoded?.name ||
        "Staff";

      const performedById =
        loggedPerson?.userId ?? tokenDecoded?.oid ?? "unknown";

      const performedByEmail =
        loggedPerson?.firmEmail ?? tokenDecoded?.emails?.[0] ?? undefined;

      try {
        await logInvoiceEvent({
          ...input,
          performedAt: input.performedAt ?? new Date().toISOString(),
          performedById,
          performedByName,
          performedByEmail,
        });
      } catch (error) {
        console.error("Error logging invoice audit event:", error);
      }
    },
    [loggedPerson, tokenDecoded],
  );

  return { logEvent };
}
